import { useEffect, useRef, useState } from 'react';
import { copiarTexto } from '@/lib/copiar';

/**
 * Botão "copiar" com as duas saídas honestas (VD):
 *
 *   copiou   → o rótulo vira "Copiado ✓" por 1,5 s (e um `aria-live` avisa);
 *   falhou   → o texto aparece num `<textarea readOnly>` JÁ SELECIONADO, com a
 *              instrução de copiar à mão.
 *
 * "Ver texto" existe sempre, para quem quer conferir antes de colar. Nunca
 * afirma "copiado" sem ter copiado — é o valor de retorno de `copiarTexto` que
 * decide, não a ausência de erro.
 *
 * ★★ A2 (02/11/2026) — `montarTexto` pode devolver uma PROMESSA (o resumo dos
 * Indicadores lê o dia de novo no servidor). Enquanto ela corre, o botão diz
 * "Atualizando…" e não aceita outro clique. ⚠ Se ela falhar, aparece `textoDoErro`
 * (ou o `paraTela` do erro) e NADA é copiado nem oferecido para cópia manual: um
 * texto velho colado no grupo é exatamente o que a busca existe para evitar.
 */
export default function BotaoCopiar({
  montarTexto, rotulo = 'Copiar relatório', className = 'btn-secundario',
  textoDoErro = 'Não foi possível montar o texto. Nada foi copiado.',
}) {
  const [estado, setEstado] = useState('pronto'); // pronto | buscando | copiado | falhou | ver | erro
  const [erro, setErro] = useState('');
  const [texto, setTexto] = useState('');
  const areaRef = useRef(null);

  useEffect(() => {
    if (estado !== 'copiado') return undefined;
    const t = setTimeout(() => setEstado('pronto'), 1500);
    return () => clearTimeout(t);
  }, [estado]);

  useEffect(() => {
    if (estado === 'falhou' && areaRef.current) {
      areaRef.current.focus();
      areaRef.current.select();
    }
  }, [estado]);

  /** O texto, esperando a promessa quando há uma; `null` quando ela falhou (o estado já diz o erro). */
  async function obterTexto() {
    const r = montarTexto();
    if (!r || typeof r.then !== 'function') return r;
    setEstado('buscando');
    try {
      return await r;
    } catch (e) {
      setTexto('');
      setErro(e?.paraTela || textoDoErro);
      setEstado('erro');
      return null;
    }
  }

  async function copiar() {
    const t = await obterTexto();
    if (t === null) return;
    setTexto(t);
    const ok = await copiarTexto(t);
    setEstado(ok ? 'copiado' : 'falhou');
  }

  async function verTexto() {
    if (estado === 'ver') { setEstado('pronto'); return; }
    const t = await obterTexto();
    if (t === null) return;
    setTexto(t);
    setEstado('ver');
  }

  const mostrarArea = estado === 'falhou' || estado === 'ver';

  return (
    <div className="botao-copiar">
      <div className="linha">
        <button type="button" className={className} onClick={copiar} disabled={estado === 'buscando'}>
          {estado === 'copiado' ? 'Copiado ✓' : estado === 'buscando' ? 'Atualizando…' : rotulo}
        </button>
        <button
          type="button"
          className="btn-texto"
          onClick={verTexto}
          disabled={estado === 'buscando'}
        >
          {estado === 'ver' ? 'ocultar' : 'ver texto'}
        </button>
        <span className="visualmente-oculto" aria-live="polite">
          {estado === 'copiado' ? 'Relatório copiado.' : ''}
        </span>
      </div>

      {estado === 'erro' && (
        <p className="erro" role="alert">{erro}</p>
      )}

      {estado === 'falhou' && (
        <p className="campo-ajuda" role="alert">
          Não foi possível copiar automaticamente. O texto abaixo já está selecionado —
          copie à mão.
        </p>
      )}

      {mostrarArea && (
        <textarea
          ref={areaRef}
          readOnly
          className="relatorio-texto"
          rows={Math.min(14, texto.split('\n').length + 1)}
          value={texto}
          aria-label="Texto do relatório"
        />
      )}
    </div>
  );
}
